import React, { useState } from 'react';
import { X, Plus, FileText, Link, Video, PenTool as Tool, CheckSquare, BookOpen } from 'lucide-react';
import { useResourceStore } from '../../store/resourceStore';
import type { CreateResourceForm } from '../../types/resources';
import { ResourceType, DifficultyLevel } from '../../types/resources';

interface ResourceCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ResourceCreateModal: React.FC<ResourceCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { categories, createResource, isLoading } = useResourceStore();
  const [formData, setFormData] = useState<CreateResourceForm>({
    title: '',
    description: '',
    content: '',
    type: ResourceType.ARTICLE,
    category: '',
    tags: [],
    difficulty_level: DifficultyLevel.BEGINNER,
    external_url: '',
    is_featured: false
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    if (!formData.content.trim() && !formData.external_url.trim()) {
      newErrors.content = 'Le contenu ou un lien externe est requis';
    }
    if (!formData.category) {
      newErrors.category = 'La catégorie est requise';
    }
    if (formData.tags.length === 0) {
      newErrors.tags = 'Au moins un tag est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await createResource(formData);
      onSuccess();
      onClose();
      // Reset form
      setFormData({
        title: '',
        description: '',
        content: '',
        type: ResourceType.ARTICLE,
        category: '',
        tags: [],
        difficulty_level: DifficultyLevel.BEGINNER,
        external_url: '',
        is_featured: false
      });
      setTagInput('');
      setErrors({});
    } catch (error) {
      console.error('Error creating resource:', error);
    }
  };

  const handleInputChange = (field: keyof CreateResourceForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({ 
      ...prev, 
      tags: prev.tags.filter(tag => tag !== tagToRemove) 
    }));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const getTypeIcon = (type: ResourceType) => {
    switch (type) {
      case ResourceType.ARTICLE: return <FileText className="w-4 h-4" />;
      case ResourceType.VIDEO: return <Video className="w-4 h-4" />;
      case ResourceType.DOCUMENT: return <BookOpen className="w-4 h-4" />;
      case ResourceType.LINK: return <Link className="w-4 h-4" />;
      case ResourceType.TOOL: return <Tool className="w-4 h-4" />;
      case ResourceType.CHECKLIST: return <CheckSquare className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Créer une nouvelle ressource</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Titre *
            </label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.title ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: Guide du Business Model Canvas pour l'ESS"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Décrivez brièvement le contenu et l'utilité de cette ressource..."
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Type and Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                Type de ressource *
              </label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value as ResourceType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={ResourceType.ARTICLE}>📄 Article</option>
                <option value={ResourceType.GUIDE}>📖 Guide</option>
                <option value={ResourceType.CHECKLIST}>✅ Checklist</option>
                <option value={ResourceType.TOOL}>🛠️ Outil</option>
                <option value={ResourceType.VIDEO}>🎥 Vidéo</option>
                <option value={ResourceType.DOCUMENT}>📋 Document</option>
                <option value={ResourceType.LINK}>🔗 Lien</option>
              </select>
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Thématique *
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.category ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionnez une thématique</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.slug}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category}</p>
              )}
            </div>
          </div>

          {/* Difficulty */}
          <div>
            <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-2">
              Niveau de difficulté
            </label>
            <select
              id="difficulty"
              value={formData.difficulty_level}
              onChange={(e) => handleInputChange('difficulty_level', e.target.value as DifficultyLevel)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={DifficultyLevel.BEGINNER}>🟢 Débutant</option>
              <option value={DifficultyLevel.INTERMEDIATE}>🟡 Intermédiaire</option>
              <option value={DifficultyLevel.ADVANCED}>🟠 Avancé</option>
              <option value={DifficultyLevel.EXPERT}>🔴 Expert</option>
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mots-clés *
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagKeyPress}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ajouter un mot-clé..."
              />
              <button
                type="button"
                onClick={addTag}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, index) => (
                <span key={index} className="flex items-center space-x-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-md">
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            {errors.tags && (
              <p className="mt-1 text-sm text-red-600">{errors.tags}</p>
            )}
          </div>

          {/* External URL */}
          <div>
            <label htmlFor="external_url" className="block text-sm font-medium text-gray-700 mb-2">
              Lien externe (optionnel)
            </label>
            <input
              id="external_url"
              type="url"
              value={formData.external_url}
              onChange={(e) => handleInputChange('external_url', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://exemple.com/ressource"
            />
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Contenu {!formData.external_url && '*'}
            </label>
            <textarea
              id="content"
              value={formData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              rows={12}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.content ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Rédigez le contenu de votre ressource en Markdown..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Vous pouvez utiliser la syntaxe Markdown pour formater votre contenu
            </p>
            {errors.content && (
              <p className="mt-1 text-sm text-red-600">{errors.content}</p>
            )}
          </div>

          {/* Featured */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_featured"
              checked={formData.is_featured}
              onChange={(e) => handleInputChange('is_featured', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is_featured" className="text-sm text-gray-700">
              Mettre en avant cette ressource
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Création...</span>
                </div>
              ) : (
                'Créer la ressource'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};